import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { MatDialog } from '@angular/material/dialog';

import { Observable, of as observableOf, Subscription } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { DataTypeComponent } from '../../../data-type-component';
import { FieldConfigDTO, FormRowConfigDTO } from '../../../../../../system/system-dtos';
import { SiteTreeFileDatabaseService } from './component/service/site-tree-file-database.service';

import {
  FileFlatNode,
  SeoCreatePageRequestDO,
  SeoEntityDTO,
  SeoGetPageRequestDO,
  SeoTreeContentDO,
  SeoPageDO,
  SeoPageResponseDO,
  SeoSiteMapDO,
  SeoUpdateDisplayOrderRequestDO,
  SeoUpdatePageRequestDO,
  SeoSiteMapRequestDO,
} from './component/model/site-tree.model';
import { arrayNotEmpty, isDefined, notDefined } from '../../../../../utils/utils';
import { ConfirmationModalComponent } from '../../../../../dialogs/confirmation-modal/confirmation-modal.component';
import { MyErrorStateMatcher } from '../../../../../../common/field-validation/my-error-state-matcher';

@Component({
  selector: 'app-form-field-site-tree',
  templateUrl: './site-tree-form-field.component.html',
  styleUrls: ['./site-tree-form-field.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SiteTreeFormFieldComponent extends DataTypeComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;

  @Input() fieldConfig: FieldConfigDTO;

  @Input() data: any;

  @Input() editMode: boolean;

  rowData: any;

  loading: boolean = true;

  creatingNew: boolean = false;

  activeNodeLoading: boolean = false;

  isEdit: boolean = false;

  showContent: boolean = true;

  public settingsFields: FieldConfigDTO[] = [];

  public contentFields: FieldConfigDTO[] = [];

  public formGroupSubset: UntypedFormGroup;

  activeNode: FileFlatNode;

  hoveredNode: FileFlatNode;

  treeControl: FlatTreeControl<FileFlatNode>;

  treeFlattener: MatTreeFlattener<FileFlatNode, FileFlatNode>;

  dataSource: MatTreeFlatDataSource<FileFlatNode, FileFlatNode>;

  expandedNodeSet = new Set<string>();

  dragging = false;

  expandTimeout: any;

  expandDelay = 1000;

  subscription: Subscription;

  constructor(
    private domSanitizer: DomSanitizer,
    private database: SiteTreeFileDatabaseService,
    private http: HttpClient,
    private dialog: MatDialog
  ) {
    super();
    this.treeFlattener = new MatTreeFlattener(this.transformer, this._getLevel, this._isExpandable, this._getChildren);
    this.treeControl = new FlatTreeControl<FileFlatNode>(this._getLevel, this._isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    this.formGroupSubset = new UntypedFormGroup({});
  }

  setRowData(entity: any) {
    this.rowData = entity;
  }

  ngOnInit() {
    this.getSiteMapData();
  }

  getSiteMapData() {
    this.http
      .post<SeoSiteMapDO>('/seo/sitemap', { id: this.data } as SeoSiteMapRequestDO)
      .subscribe((response: SeoSiteMapDO) => {
        this.loadPages(response.pages);
      });
  }

  loadPages(pages: SeoPageDO[]) {
    const sortedPages: SeoPageDO[] = this.sort(pages);
    this.database.initialize(sortedPages);
    // this.formGroupSubset = new FormGroup({});
    if (isDefined(this.subscription)) {
      this.subscription.unsubscribe();
    }
    this.subscription = this.database.dataChange.subscribe((data) => {
      this.rebuildTreeForData(data);
      this.loading = false;
    });
  }

  ngOnDestroy() {}

  onFlip(node: FileFlatNode) {
    this.showContent = !this.showContent;
  }

  onClick(node: FileFlatNode) {
    if (notDefined(this.activeNode) || this.activeNode.id !== node.id) {
      this.activeNode = node;
      this.getNode(this.activeNode);
    }
  }

  onNodeSelected(node: FileFlatNode) {
    this.onClick(node);
  }

  onEditClicked(node: FileFlatNode) {
    this.isEdit = true;
  }

  createNewNode() {
    const request = {
      page: this.activeNode.entity as SeoEntityDTO,
    } as SeoCreatePageRequestDO;
    this.post('/seo/createpage', request);
  }

  saveNode() {
    const request = {
      page: this.formGroupSubset.getRawValue() as SeoEntityDTO,
    } as SeoUpdatePageRequestDO;
    this.post('/seo/update', request).then(() => {
      this.isEdit = false;
    });
  }

  updatePosition(parentId: number, id: number, newParentId: number) {
    const request = {
      currentParentId: parentId,
      currentId: id,
      newParentId,
    } as SeoUpdateDisplayOrderRequestDO;
    this.post('/seo/updateposition', request);
  }

  post(url: string, request: any): Promise<boolean> {
    this.activeNodeLoading = true;
    return new Promise<boolean>((resolve, reject) => {
      this.http.post<SeoPageResponseDO>(url, request).subscribe((response: SeoPageResponseDO) => {
        this.postProcessSeoPage(response.page, response.siteMap);
        this.activeNodeLoading = false;
        resolve(true);
      });
    });
  }

  postProcessSeoPage(page: SeoTreeContentDO, siteMap: SeoSiteMapDO) {
    if (isDefined(siteMap) && isDefined(siteMap.pages)) {
      this.loadPages(siteMap.pages);
    }
    this.createFormGroupsIfNeeded(page, page.settingFields, page.contentFields);
  }

  getNode(node: FileFlatNode) {
    setTimeout(() => {
      const request = {
        id: node.entity.id,
      } as SeoGetPageRequestDO;
      this.post('/seo/get', request);
    });
  }

  editNode(node: FileFlatNode) {
    this.isEdit = true;
  }

  cancelUpdating() {
    this.isEdit = false;
  }

  onNewClicked(node: FileFlatNode) {
    this.creatingNew = true;
  }

  onDeleteClicked(node: FileFlatNode) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      data: {
        note: 'Are you sure you want to delete this page?',
      },
    });
  }

  private createFormGroupsIfNeeded(
    entity: SeoTreeContentDO,
    settingsFields: FormRowConfigDTO[],
    contentFields: FormRowConfigDTO[]
  ) {
    // Object.keys(this.formGroupSubset.controls).forEach(c => this.formGroupSubset.removeControl(c));
    const formGroupBuilder = {};

    if (arrayNotEmpty(settingsFields)) {
      const allFields = settingsFields
        .map((r) => r.columns)
        .reduce((acc, elem) => acc.concat(elem), [])
        .map((c) => c.fields)
        .reduce((acc, elem) => acc.concat(elem), []);
      if (arrayNotEmpty(allFields)) {
        if (this.isEdit) {
          this.settingsFields = allFields.filter((f) => !f.editHide).filter((f) => f.name.toLowerCase() !== 'content');
        } else {
          this.settingsFields = allFields
            .filter((f) => !f.ignoreIfNotPresent)
            .filter((f) => f.name.toLowerCase() !== 'content');
        }
      } else {
        this.settingsFields = [];
      }

      this.settingsFields.forEach((f) => {
        const formControl = this.getFormControlForField(f, entity);
        this.formGroupSubset.setControl(f.name, formControl);
      });
    }

    if (arrayNotEmpty(contentFields)) {
      const allFields = contentFields
        .map((r) => r.columns)
        .reduce((acc, elem) => acc.concat(elem), [])
        .map((c) => c.fields)
        .reduce((acc, elem) => acc.concat(elem), []);
      if (arrayNotEmpty(allFields)) {
        if (this.isEdit) {
          this.contentFields = allFields.filter(
            (f) => f.name.toLowerCase() === 'content' || f.name.toLowerCase() !== 'id'
          );
        } else {
          this.contentFields = allFields.filter(
            (f) => f.name.toLowerCase() === 'content' || f.name.toLowerCase() !== 'id'
          );
        }
      } else {
        this.contentFields = [];
      }

      this.contentFields.forEach((f) => {
        const formControl = this.getFormControlForField(f, entity);
        this.formGroupSubset.setControl(f.name, formControl);
      });
    }
  }

  private getFormControlForField(f: FieldConfigDTO, entity: SeoTreeContentDO) {
    const disabled = f.editReadOnly && !!eval(f.editReadOnly);
    const value = notDefined(entity) ? undefined : entity[f.name];

    const validators = MyErrorStateMatcher.convertToAngularValidators([f]);
    return new UntypedFormControl(
      {
        value,
        disabled,
      },
      validators
    );
  }

  transformer = (node: FileFlatNode, level: number) => {
    const clone = JSON.parse(JSON.stringify(node));
    clone.expandable = !!node.children;
    clone.level = level;
    return clone;
  };

  private _getLevel = (node: FileFlatNode) => node.level;

  private _isExpandable = (node: FileFlatNode) => node.expandable;

  private _getChildren = (node: FileFlatNode): Observable<FileFlatNode[]> => observableOf(node.children);

  hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;

  /**
   * This constructs an array of nodes that matches the DOM,
   * and calls rememberExpandedTreeNodes to persist expand state
   */
  visibleNodes(): FileFlatNode[] {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    const result = [];

    function addExpandedChildren(node: FileFlatNode, expanded: Set<string>) {
      result.push(node);
      if (expanded.has(node.id)) {
        node.children.map((child) => addExpandedChildren(child, expanded));
      }
    }

    this.dataSource.data.forEach((node) => {
      addExpandedChildren(node, this.expandedNodeSet);
    });
    return result;
  }

  /**
   * Handle the drop - here we rearrange the data based on the drop event,
   * then rebuild the tree.
   * */
  drop(event: CdkDragDrop<string[]>) {
    // ignore drops outside of the tree
    if (!event.isPointerOverContainer) {
      return;
    }

    // construct a list of visible nodes, this will match the DOM.
    // the cdkDragDrop event.currentIndex jives with visible nodes.
    // it calls rememberExpandedTreeNodes to persist expand state
    const visibleNodes = this.visibleNodes();

    // deep clone the data source so we can mutate it
    const changedData = JSON.parse(JSON.stringify(this.dataSource.data));

    // recursive find function to find siblings of node
    function findNodeSiblings(arr: Array<any>, id: string): Array<any> {
      let result;
      let subResult;
      arr.forEach((item) => {
        if (item.id === id) {
          result = arr;
        } else if (item.children) {
          subResult = findNodeSiblings(item.children, id);
          if (subResult) {
            result = subResult;
          }
        }
      });
      return result;
    }

    // remove the node from its old place
    const node = event.item.data;
    const siblings = findNodeSiblings(changedData, node.id);
    const siblingIndex = siblings.findIndex((n) => n.id === node.id);
    const nodeToInsert: FileFlatNode = siblings.splice(siblingIndex, 1)[0];

    // determine where to insert the node
    const nodeAtDest = visibleNodes[event.currentIndex];
    if (nodeAtDest.id === nodeToInsert.id) {
      return;
    }

    // determine drop index relative to destination array
    let relativeIndex = event.currentIndex; // default if no parent
    const nodeAtDestFlatNode = this.treeControl.dataNodes.find((n) => nodeAtDest.id === n.id);

    const parent = this.getParentNode(nodeAtDestFlatNode);
    if (parent) {
      const parentIndex = visibleNodes.findIndex((n) => n.id === parent.id) + 1;
      relativeIndex = event.currentIndex - parentIndex;
    } else {
      alert('not allowed');
      return;
    }

    // insert node
    const newSiblings = findNodeSiblings(changedData, nodeAtDest.id);
    if (!newSiblings) {
      return;
    }
    this.updatePosition(parent.entity.parentId, parent.entity.id, nodeToInsert.entity.parentId);
  }

  /**
   * Experimental - opening tree nodes as you drag over them
   */
  dragStart() {
    this.dragging = true;
  }

  dragEnd() {
    this.dragging = false;
  }

  dragHover(node: FileFlatNode) {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = setTimeout(() => {
        this.treeControl.expand(node);
      }, this.expandDelay);
    }
    node.hovered = true;
    this.hoveredNode = node;
  }

  dragHoverEnd(node: FileFlatNode) {
    if (this.dragging) {
      clearTimeout(this.expandTimeout);
    }
    node.hovered = false;
    if (
      isDefined(node) &&
      isDefined(node.entity) &&
      isDefined(this.hoveredNode) &&
      isDefined(this.hoveredNode.entity)
    ) {
      if (node.entity.id === this.hoveredNode.entity.id) {
        this.hoveredNode = null;
      }
    }
  }

  /**
   * The following methods are for persisting the tree expand state
   * after being rebuilt
   */
  rebuildTreeForData(data: any) {
    this.rememberExpandedTreeNodes(this.treeControl, this.expandedNodeSet);
    this.dataSource.data = data;
    this.forgetMissingExpandedNodes(this.treeControl, this.expandedNodeSet);
    this.expandNodesById(this.treeControl.dataNodes, Array.from(this.expandedNodeSet));
  }

  private rememberExpandedTreeNodes(treeControl: FlatTreeControl<FileFlatNode>, expandedNodeSet: Set<string>) {
    if (treeControl.dataNodes) {
      treeControl.dataNodes.forEach((node) => {
        if (treeControl.isExpandable(node) && treeControl.isExpanded(node)) {
          // capture latest expanded state
          expandedNodeSet.add(node.id);
        }
      });
    }
  }

  private forgetMissingExpandedNodes(treeControl: FlatTreeControl<FileFlatNode>, expandedNodeSet: Set<string>) {
    if (treeControl.dataNodes) {
      expandedNodeSet.forEach((nodeId) => {
        // maintain expanded node state
        if (!treeControl.dataNodes.find((n) => n.id === nodeId)) {
          // if the tree doesn't have the previous node, remove it from the expanded list
          expandedNodeSet.delete(nodeId);
        }
      });
    }
  }

  private expandNodesById(flatNodes: FileFlatNode[], ids: string[]) {
    if (!flatNodes || flatNodes.length === 0) {
      return;
    }
    const idSet = new Set(ids);
    return flatNodes.forEach((node) => {
      if (idSet.has(node.id)) {
        this.treeControl.expand(node);
        let parent = this.getParentNode(node);
        while (parent) {
          this.treeControl.expand(parent);
          parent = this.getParentNode(parent);
        }
      }
    });
  }

  private getParentNode(node: FileFlatNode): FileFlatNode | null {
    const currentLevel = node.level;
    if (currentLevel < 1) {
      return null;
    }
    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;
    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];
      if (currentNode.level < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  sort(pages: SeoPageDO[]): SeoPageDO[] {
    if (arrayNotEmpty(pages)) {
      const sortedData: SeoPageDO[] = pages.sort((a: SeoPageDO, b: SeoPageDO) => a.displayOrder - b.displayOrder);
      sortedData.forEach((page: SeoPageDO) => {
        if (arrayNotEmpty(page.children)) {
          const sortedChildren: SeoPageDO[] = page.children.sort(
            (a: SeoPageDO, b: SeoPageDO) => a.displayOrder - b.displayOrder
          );
          page.children = sortedChildren;
          page.children.forEach((childPage: SeoPageDO) => {
            if (arrayNotEmpty(childPage.children)) {
              const sortedChildren: SeoPageDO[] = childPage.children.sort(
                (a: SeoPageDO, b: SeoPageDO) => a.displayOrder - b.displayOrder
              );
              childPage.children = sortedChildren;
            }
          });
        }
      });
      return sortedData;
    }
    return [];
  }
}
